import { useMemo } from "react";
import { cn } from "@/lib/utils";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Sparkles, Bot, Smile, AlertTriangle, Zap,
  HelpCircle, ThumbsUp, Heart, Laugh,
} from "lucide-react";

/** Expression states for the GM avatar. */
export type GMExpression = "neutral" | "thinking" | "happy" | "concerned" | "excited" | "amused";

/** Per-expression configuration: icon, colors, animation. */
const EXPRESSION_CONFIG: Record<GMExpression, {
  icon: React.ElementType;
  bgClass: string;
  iconClass: string;
  animateClass: string;
  label: string;
}> = {
  neutral: {
    icon: Bot,
    bgClass: "bg-primary/10",
    iconClass: "text-primary/70",
    animateClass: "",
    label: "Game Master",
  },
  thinking: {
    icon: HelpCircle,
    bgClass: "bg-amber-500/10",
    iconClass: "text-amber-500",
    animateClass: "animate-pulse",
    label: "Game Master thinking…",
  },
  happy: {
    icon: Smile,
    bgClass: "bg-green-500/10",
    iconClass: "text-green-500",
    animateClass: "",
    label: "Game Master",
  },
  concerned: {
    icon: AlertTriangle,
    bgClass: "bg-orange-500/10",
    iconClass: "text-orange-500",
    animateClass: "",
    label: "Game Master",
  },
  excited: {
    icon: Zap,
    bgClass: "bg-yellow-500/10",
    iconClass: "text-yellow-500",
    animateClass: "animate-bounce",
    label: "Game Master",
  },
  amused: {
    icon: Laugh,
    bgClass: "bg-pink-500/10",
    iconClass: "text-pink-500",
    animateClass: "",
    label: "Game Master",
  },
};

/**
 * Derive the GM expression from message metadata and adventure state.
 * Looks at intent, combat, validity, director notes, and tone.
 */
export function deriveGMExpression(metadata: Record<string, any> | undefined, contextTags?: string[]): GMExpression {
  if (!metadata) return "neutral";

  const classification = metadata.intent_classification as Record<string, any> | undefined;
  const validity = metadata.validity_check as Record<string, any> | undefined;
  const director = metadata.director_notes as Record<string, any> | undefined;

  // Anti-cheat rejection → amused ("nice try!")
  if (validity && validity.is_valid === false) return "amused";

  // Combat scene → excited
  if (contextTags?.includes("combat")) return "excited";

  // Dice roll with high result → happy
  if (Array.isArray(metadata.dice_rolls) && metadata.dice_rolls.length > 0) {
    const highRoll = metadata.dice_rolls.some((d: any) => d.result >= 18);
    if (highRoll) return "excited";
  }

  // Mercy event → concerned/caring
  if (director?.mercy_event) return "concerned";

  // High echo risk or rich narrative → thinking
  if (classification?.echo_risk) return "thinking";

  // GM query response → thinking (helpful mode)
  if (classification?.primary_intent === "GM_QUERY") return "thinking";

  // Dialogue/social → happy/warm
  if (classification?.primary_intent === "DIALOGUE" || classification?.primary_intent === "SOCIAL") return "happy";

  // Exploration → neutral (observant)
  return "neutral";
}

// ── Component ──

interface GMAvatarProps {
  /** Pre-set expression. If omitted, derives from metadata. */
  expression?: GMExpression;
  /** Message metadata used to auto-derive expression. */
  metadata?: Record<string, any>;
  /** Adventure context tags (e.g. "combat"). */
  contextTags?: string[];
  /** Custom image URL (set by world builder in editor). */
  imageUrl?: string;
  /** Show a label below the avatar. Default true. */
  showLabel?: boolean;
  /** Size variant. */
  size?: "sm" | "md";
}

/**
 * The Game Master avatar — a friendly, expressive presence that
 * accompanies every narrator message. Supports multiple emotional
 * states with distinct icons, colors, and subtle animations.
 *
 * Future: world builders can upload custom avatar images per
 * expression state (happy GM, concerned GM, etc.).
 */
export function GMAvatar({
  expression: forcedExpression,
  metadata,
  contextTags,
  imageUrl,
  showLabel = true,
  size = "sm",
}: GMAvatarProps) {
  const derivedExpression = useMemo(
    () => forcedExpression ?? deriveGMExpression(metadata, contextTags),
    [forcedExpression, metadata, contextTags],
  );

  const config = EXPRESSION_CONFIG[derivedExpression];
  const Icon = config.icon;

  const sizeClasses = size === "md"
    ? { avatar: "h-10 w-10", icon: "w-5 h-5", label: "text-[11px]" }
    : { avatar: "h-7 w-7", icon: "w-3.5 h-3.5", label: "text-[10px]" };

  return (
    <div className="flex items-center gap-2 shrink-0">
      <Avatar className={cn(sizeClasses.avatar, config.bgClass, config.animateClass, "ring-1 ring-border/40")}>
        {imageUrl ? (
          <AvatarImage src={imageUrl} alt="Game Master" />
        ) : null}
        <AvatarFallback className={cn(config.bgClass, "rounded-full")}>
          <Icon className={cn(sizeClasses.icon, config.iconClass)} />
        </AvatarFallback>
      </Avatar>
      {showLabel && (
        <span className={cn(sizeClasses.label, "text-muted-foreground/60 font-medium select-none")}>
          {config.label}
        </span>
      )}
    </div>
  );
}
